import type { PlayerItemVisitResponse } from '@on3/api';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import clsx from 'clsx';
import { EarlyEnrolleeIcon } from 'components/Icons/EarlyEnrolleeIcon';
import { EarlySigneeIcon } from 'components/Icons/EarlySigneeIcon';
import { On3Avatar } from 'components/Image/On3Avatar';
import { StatusContainer } from 'components/RecruitmentStatus/StatusContainer';
import { UndecidedStatus } from 'components/RecruitmentStatus/UndecidedStatus';
import { AthleteVerified } from 'components/UI/AthleteVerified/AthleteVerified';
import { TextLink } from 'components/UI/Link/TextLink';
import { StarRating } from 'components/UI/StarRating/StarRating';
import { Text } from 'components/UI/Typography/Text';
import { useRouter } from 'next/router';

import styles from './VisitListItem.module.scss';

export const VisitListItem = ({
  rating,
  player,
  status,
  nilValue,
  rpm,
  official,
}: PlayerItemVisitResponse) => {
  const {
    fullName,
    slug,
    hometown,
    state,
    earlyEnrollee,
    earlySignee,
    defaultAsset,
    height,
    weight,
    athleteVerified,
    highSchool,
    position,
  } = player || {};
  const { type, transferredAsset, committedAsset } = status || {};

  const router = useRouter();
  const { year, sport } = router.query;
  const hasStatus = status && type !== 'None' && committedAsset?.slug;
  const hasPredictions = !!rpm?.length;
  const showIcons = earlyEnrollee || earlySignee;
  const showNil = !!nilValue?.totalValue && nilValue?.totalValue >= 5000;
  const isTransfer = transferredAsset?.slug;

  return (
    <div
      className={clsx(styles.playerWrapper, {
        [styles.isTransfer]: isTransfer,
      })}
    >
      <On3Avatar
        altText={`${fullName} Avatar`}
        className={styles.avatar}
        domain={defaultAsset?.domain}
        source={defaultAsset?.source}
      />

      {official && (
        <Text className={styles.official} variant="subtitle1">
          Official
        </Text>
      )}
      <div className={styles.name}>
        <TextLink color="primary" href={`/db/${slug}/`} variant="h5">
          {fullName}
        </TextLink>
        {athleteVerified && <AthleteVerified />}
      </div>
      <Text className={styles.hometownHighschool}>
        <TextLink
          className={styles.highSchool}
          href={`/high-school/${highSchool?.urlSlug}/`}
        >
          {highSchool?.name}
        </TextLink>
        <span className={styles.homeTownName}>
          ({hometown?.name}, {state?.abbr})
        </span>
      </Text>

      {showIcons && (
        <div
          className={clsx(styles.earlyIconsContainer, {
            [styles.isOfficial]: official,
          })}
        >
          {earlyEnrollee && <EarlyEnrolleeIcon className={styles.earlyIcon} />}
          {earlySignee && <EarlySigneeIcon className={styles.earlyIcon} />}
        </div>
      )}

      <Text className={styles.playerVitals}>
        <span className={styles.position}>{position?.abbr}</span>
        <span className={styles.height}>{height}</span>
        <span>{weight}</span>
      </Text>

      <StarRating
        className={styles.stars}
        fiveStarPlus={rating?.fiveStarPlus}
        href={`/db/${slug}/industry-comparison/`}
        isConsensus
        nationalRank={rating?.consensusNationalRank}
        position={position?.abbr}
        positionRank={rating?.consensusPositionRank}
        rating={rating?.consensusRating}
        rounding={2}
        sport={`${sport}`}
        stars={rating?.consensusStars}
        state={rating?.stateAbbr}
        stateRank={rating?.consensusStateRank}
        year={`${year}`}
      />

      {showNil && nilValue.totalValue ? (
        <div className={styles.nilContainer}>
          <Text className={styles.nilLabel}>NIL Value:</Text>
          <Text className={styles.nilValuation}>
            {formatCurrency(nilValue.totalValue)}
          </Text>
        </div>
      ) : (
        <Text className={styles.valuationUndisclosed}>undisclosed</Text>
      )}

      <div
        className={clsx(styles.statusWrapper, {
          [styles.isTransfer]: isTransfer,
        })}
      >
        {(hasStatus || hasPredictions) && (
          <StatusContainer
            player={player}
            rpm={rpm}
            sport={rating?.sport}
            status={status}
          />
        )}

        {/* No status and No Predictions show Undecided */}
        {!hasStatus && !hasPredictions && <UndecidedStatus />}
      </div>
    </div>
  );
};
